import React, { useCallback, useEffect, useState } from 'react';
import ComponentCard from '../../components/common/ComponentCard';
import Label from '../../components/form/Label';
import Input from '../../components/form/input/InputField';
import { api, apiEndpoints } from '../../utils/apiService';

const DEFAULT_NOTIFY_EMAIL = 'sp@askarthur.io';

interface DemoLineCaller {
  id: number;
  caller_phone: string;
  display_phone: string;
  caller_name: string;
  caller_email: string;
  call_count: number;
  last_called_at: string | null;
  last_alerted_at: string | null;
}

interface DemoLine {
  id: number;
  phone_number: string;
  display_phone: string;
  label: string;
  notify_email: string;
  is_active: boolean;
  callers: DemoLineCaller[];
}

const formatWhen = (value: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const callCountLabel = (count: number) =>
  `${count} ${count === 1 ? 'time' : 'times'}`;

const DemoLinesTab: React.FC = () => {
  const [lines, setLines] = useState<DemoLine[]>([]);
  const [phone, setPhone] = useState('');
  const [label, setLabel] = useState('');
  const [notifyEmail, setNotifyEmail] = useState(DEFAULT_NOTIFY_EMAIL);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadLines = useCallback(async () => {
    setLoading(true);
    setError('');
    const response = await api.get(apiEndpoints.demoLines());
    if (!response?.ok) {
      setError('Could not load demo lines.');
      setLoading(false);
      return;
    }
    const data = (await response.json()) as DemoLine[];
    setLines(Array.isArray(data) ? data : []);
    setLoading(false);
  }, []);

  useEffect(() => {
    void loadLines();
  }, [loadLines]);

  const handleAdd = async () => {
    const trimmedPhone = phone.trim();
    if (!trimmedPhone) {
      setError('Enter the demo line phone number.');
      return;
    }
    setSaving(true);
    setError('');
    setSuccess('');
    const response = await api.post(apiEndpoints.demoLines(), {
      phone_number: trimmedPhone,
      label: label.trim(),
      notify_email: notifyEmail.trim() || DEFAULT_NOTIFY_EMAIL,
    });
    setSaving(false);
    if (!response?.ok) {
      let message = 'Could not add that demo line.';
      try {
        const body = await response?.json();
        const phoneError = body?.phone_number;
        if (Array.isArray(phoneError) && phoneError[0]) {
          message = String(phoneError[0]);
        } else if (typeof phoneError === 'string') {
          message = phoneError;
        }
      } catch {
        // The response body is not JSON.
      }
      setError(message);
      return;
    }
    setPhone('');
    setLabel('');
    setNotifyEmail(DEFAULT_NOTIFY_EMAIL);
    setSuccess('Demo line added. Calls to it will email the contact.');
    await loadLines();
  };

  const handleRemove = async (line: DemoLine) => {
    if (!window.confirm(`Stop watching ${line.display_phone}?`)) return;
    setError('');
    setSuccess('');
    const response = await api.delete(apiEndpoints.demoLine(line.id));
    if (!response?.ok && response?.status !== 204) {
      setError('Could not remove that demo line.');
      return;
    }
    await loadLines();
  };

  return (
    <ComponentCard title="Demo Lines" className="overflow-hidden">
      <div className="space-y-6">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Add a demo number, such as (762) 337-6973, to see who is calling it.
          Each caller emails the contact once every 24 hours. Calls in between
          are still counted, and the next email says how many times that person
          has called.
        </p>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <div>
            <Label htmlFor="demo-line-phone">Demo number</Label>
            <Input
              id="demo-line-phone"
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
              placeholder="(762) 337-6973"
            />
          </div>
          <div>
            <Label htmlFor="demo-line-label">Label</Label>
            <Input
              id="demo-line-label"
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              placeholder="Sales demo"
            />
          </div>
          <div>
            <Label htmlFor="demo-line-email">Alert email</Label>
            <Input
              id="demo-line-email"
              type="email"
              value={notifyEmail}
              onChange={(event) => setNotifyEmail(event.target.value)}
            />
          </div>
          <div className="flex items-end">
            <button
              type="button"
              disabled={saving}
              onClick={() => void handleAdd()}
              className="h-11 w-full rounded-2xl bg-[rgb(var(--app-brand))] px-4 text-sm font-semibold text-white disabled:opacity-50"
            >
              {saving ? 'Adding…' : 'Add demo line'}
            </button>
          </div>
        </div>

        {error && (
          <div className="rounded-lg border border-error-200 bg-error-50 p-3 text-sm text-error-500">
            {error}
          </div>
        )}
        {success && (
          <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-700">
            {success}
          </div>
        )}

        {loading ? (
          <p className="text-sm text-gray-500">Loading demo lines…</p>
        ) : lines.length === 0 ? (
          <p className="text-sm text-gray-500">
            No demo lines yet. Add (762) 337-6973 to start tracing callers.
          </p>
        ) : (
          <div className="space-y-6">
            {lines.map((line) => (
              <section
                key={line.id}
                className="rounded-2xl border border-[rgb(var(--app-border))] p-4"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className="text-base font-semibold text-gray-800 dark:text-white">
                      {line.display_phone}
                      {line.label ? ` · ${line.label}` : ''}
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Alerts go to {line.notify_email}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => void handleRemove(line)}
                    className="rounded-2xl border border-[rgb(var(--app-border))] px-3 py-2 text-sm font-semibold"
                  >
                    Remove
                  </button>
                </div>

                {line.callers.length === 0 ? (
                  <p className="mt-4 text-sm text-gray-500">No callers yet.</p>
                ) : (
                  <div className="mt-4 overflow-x-auto">
                    <table className="min-w-full text-left text-sm">
                      <thead className="text-xs uppercase text-gray-500">
                        <tr>
                          <th className="py-2 pr-4">Caller</th>
                          <th className="py-2 pr-4">Name</th>
                          <th className="py-2 pr-4">Email</th>
                          <th className="py-2 pr-4">Calls</th>
                          <th className="py-2 pr-4">Last call</th>
                          <th className="py-2">Last alert</th>
                        </tr>
                      </thead>
                      <tbody>
                        {line.callers.map((caller) => (
                          <tr
                            key={caller.id}
                            className="border-t border-[rgb(var(--app-border))]"
                          >
                            <td className="py-2 pr-4">
                              {caller.display_phone}
                            </td>
                            <td className="py-2 pr-4">
                              {caller.caller_name || '—'}
                            </td>
                            <td className="py-2 pr-4">
                              {caller.caller_email || '—'}
                            </td>
                            <td className="py-2 pr-4">
                              {callCountLabel(caller.call_count)}
                            </td>
                            <td className="py-2 pr-4">
                              {formatWhen(caller.last_called_at)}
                            </td>
                            <td className="py-2">
                              {formatWhen(caller.last_alerted_at)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            ))}
          </div>
        )}
      </div>
    </ComponentCard>
  );
};

export default DemoLinesTab;
